import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, Upload, RefreshCw, Check, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  fr: { selfie: "Selfie (webcam)", id_front: "Pièce d'identité — recto", id_back: "Pièce d'identité — verso", proof_of_funds: "Justificatif (facultatif)", company_registration: "Extrait Kbis / Registre de commerce" },
  en: { selfie: "Selfie (webcam)", id_front: "ID document — front", id_back: "ID document — back", proof_of_funds: "Supporting document (optional)", company_registration: "Company registration" },
  es: { selfie: "Selfie (cámara web)", id_front: "Documento de identidad — anverso", id_back: "Documento de identidad — reverso", proof_of_funds: "Justificante (opcional)", company_registration: "Registro mercantil" },
  de: { selfie: "Selfie (Webcam)", id_front: "Ausweisdokument — Vorderseite", id_back: "Ausweisdokument — Rückseite", proof_of_funds: "Nachweis (optional)", company_registration: "Handelsregisterauszug" },
  it: { selfie: "Selfie (webcam)", id_front: "Documento d'identità — fronte", id_back: "Documento d'identità — retro", proof_of_funds: "Giustificativo (facoltativo)", company_registration: "Registro delle imprese" },
  pt: { selfie: "Selfie (webcam)", id_front: "Documento de identidade — frente", id_back: "Documento de identidade — verso", proof_of_funds: "Comprovativo (opcional)", company_registration: "Registo comercial" },
  ar: { selfie: "صورة شخصية (كاميرا الويب)", id_front: "وثيقة الهوية — الوجه", id_back: "وثيقة الهوية — الظهر", proof_of_funds: "مستند داعم (اختياري)", company_registration: "شهادة السجل التجاري" },
  "zh-CN": { selfie: "自拍照（网络摄像头）", id_front: "身份证件 — 正面", id_back: "身份证件 — 反面", proof_of_funds: "证明材料（可选）", company_registration: "营业执照" },
};

const HINT = {
  fr: "Vos documents sont chiffrés au repos, accessibles uniquement à l'administration, et supprimés automatiquement 30 jours après notre décision.",
  en: "Your documents are encrypted at rest, accessible only to administrators, and automatically deleted 30 days after our decision.",
  es: "Sus documentos se conservan cifrados, son accesibles únicamente para la administración y se eliminan automáticamente 30 días después de nuestra decisión.",
  de: "Ihre Dokumente sind verschlüsselt gespeichert, nur für die Administration zugänglich und werden 30 Tage nach unserer Entscheidung automatisch gelöscht.",
  it: "I suoi documenti sono conservati in forma cifrata, accessibili solo all'amministrazione ed eliminati automaticamente 30 giorni dopo la nostra decisione.",
  pt: "Os seus documentos são armazenados de forma cifrada, acessíveis apenas à administração e eliminados automaticamente 30 dias após a nossa decisão.",
  ar: "تُحفظ وثائقكم مشفَّرة، ولا يمكن الاطلاع عليها إلا من قبل الإدارة، وتُحذف تلقائياً بعد 30 يوماً من قرارنا.",
  "zh-CN": "您的证件以加密方式保存，仅管理员可访问，并在我们作出决定 30 天后自动删除。",
};

const CTA = {
  fr: { title: "Vérification d'identité", subtitle: "Un selfie et une pièce d'identité en cours de validité suffisent.", startCamera: "Activer la caméra", capture: "Capturer", retry: "Reprendre", save: "Enregistrer le selfie", saved: "Selfie enregistré", upload: "Choisir un fichier", replace: "Remplacer", uploaded: "Envoyé", camDenied: "Accès caméra refusé — vous pouvez téléverser un selfie à la place.", cancel: "Annuler", refresh: "Rafraîchir" },
  en: { title: "Identity verification", subtitle: "A selfie plus a valid identity document is all we need.", startCamera: "Turn on camera", capture: "Capture", retry: "Retake", save: "Save selfie", saved: "Selfie saved", upload: "Choose a file", replace: "Replace", uploaded: "Uploaded", camDenied: "Camera access denied — you may upload a selfie instead.", cancel: "Cancel", refresh: "Refresh" },
  es: { title: "Verificación de identidad", subtitle: "Un selfie y un documento de identidad en vigor son suficientes.", startCamera: "Activar la cámara", capture: "Capturar", retry: "Repetir", save: "Guardar selfie", saved: "Selfie guardado", upload: "Elegir un archivo", replace: "Reemplazar", uploaded: "Enviado", camDenied: "Acceso a la cámara denegado — puede subir un selfie en su lugar.", cancel: "Cancelar", refresh: "Actualizar" },
  de: { title: "Identitätsprüfung", subtitle: "Ein Selfie und ein gültiges Ausweisdokument genügen.", startCamera: "Kamera aktivieren", capture: "Aufnehmen", retry: "Wiederholen", save: "Selfie speichern", saved: "Selfie gespeichert", upload: "Datei wählen", replace: "Ersetzen", uploaded: "Übermittelt", camDenied: "Kamerazugriff verweigert — Sie können stattdessen ein Selfie hochladen.", cancel: "Abbrechen", refresh: "Aktualisieren" },
  it: { title: "Verifica dell'identità", subtitle: "Un selfie e un documento d'identità in corso di validità sono sufficienti.", startCamera: "Attiva la fotocamera", capture: "Scatta", retry: "Rifai", save: "Salva selfie", saved: "Selfie salvato", upload: "Scegliere un file", replace: "Sostituire", uploaded: "Inviato", camDenied: "Accesso alla fotocamera negato — può caricare un selfie in alternativa.", cancel: "Annulla", refresh: "Aggiorna" },
  pt: { title: "Verificação de identidade", subtitle: "Um selfie e um documento de identificação válido são suficientes.", startCamera: "Ativar a câmara", capture: "Capturar", retry: "Repetir", save: "Guardar selfie", saved: "Selfie guardado", upload: "Escolher um ficheiro", replace: "Substituir", uploaded: "Enviado", camDenied: "Acesso à câmara negado — pode carregar um selfie em alternativa.", cancel: "Cancelar", refresh: "Atualizar" },
  ar: { title: "التحقق من الهوية", subtitle: "صورة شخصية ووثيقة هوية سارية المفعول كافيتان.", startCamera: "تفعيل الكاميرا", capture: "التقاط", retry: "إعادة المحاولة", save: "حفظ الصورة الشخصية", saved: "تم حفظ الصورة", upload: "اختيار ملف", replace: "استبدال", uploaded: "تم الإرسال", camDenied: "تم رفض الوصول إلى الكاميرا — يمكنكم رفع صورة شخصية بدلاً من ذلك.", cancel: "إلغاء", refresh: "تحديث" },
  "zh-CN": { title: "身份验证", subtitle: "只需一张自拍照和一份有效身份证件。", startCamera: "开启摄像头", capture: "拍摄", retry: "重拍", save: "保存自拍", saved: "自拍已保存", upload: "选择文件", replace: "替换", uploaded: "已上传", camDenied: "摄像头访问被拒 — 您可以改为上传自拍。", cancel: "取消", refresh: "刷新" },
};

const uiLang = (l) => {
  const raw = String(l || "fr");
  if (raw in CTA) return raw;
  const base = raw.split("-")[0].toLowerCase();
  if (base === "zh") return "zh-CN";
  return base in CTA ? base : "fr";
};

/**
 * IdentityVerificationStep
 * Renders a webcam-selfie capture and 4 file upload slots (id_front, id_back,
 * proof_of_funds, company_registration). Uploaded files are shown as chips.
 */
export const IdentityVerificationStep = ({ token, locale = "fr" }) => {
  const L = uiLang(locale);
  const KIND = KIND_LABELS[L];
  const CT = CTA[L];

  const [docs, setDocs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Webcam state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [camActive, setCamActive] = useState(false);
  const [camDenied, setCamDenied] = useState(false);
  const [captured, setCaptured] = useState(null); // dataURL
  const [savingSelfie, setSavingSelfie] = useState(false);

  // Upload state
  const [uploadingKind, setUploadingKind] = useState(null);

  const loadDocs = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/application/${token}/documents`);
      setDocs(data.items || []);
    } catch (e) {
      // ignore
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDocs();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const docFor = (kind) => docs.find((d) => d.kind === kind);

  const startCamera = async () => {
    setError(null);
    setCamDenied(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamActive(true);
    } catch (e) {
      setCamDenied(true);
    }
  };

  const stopCamera = () => {
    const v = videoRef.current;
    if (v && v.srcObject) {
      const tracks = v.srcObject.getTracks();
      tracks.forEach((t) => t.stop());
      v.srcObject = null;
    }
    setCamActive(false);
  };

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth || 720;
    c.height = v.videoHeight || 540;
    const ctx = c.getContext("2d");
    // Mirror the image horizontally so it matches the preview
    ctx.translate(c.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.9);
    setCaptured(dataUrl);
    stopCamera();
  };

  const saveSelfie = async () => {
    if (!captured) return;
    setSavingSelfie(true);
    try {
      await axios.post(`${API}/application/${token}/documents/selfie`, {
        image_base64: captured,
      });
      setCaptured(null);
      await loadDocs();
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed");
    } finally {
      setSavingSelfie(false);
    }
  };

  const uploadFile = async (kind, file) => {
    if (!file) return;
    setUploadingKind(kind);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await axios.post(`${API}/application/${token}/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadDocs();
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingKind(null);
    }
  };

  return (
    <div data-testid="wizard-kyc-step" dir={L === "ar" ? "rtl" : "ltr"}>
      <div className="text-[10px] tracking-luxe uppercase text-[#e6c98a]">Chapter — 05</div>
      <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl">
        {CT.title}
      </h2>
      <p className="mt-6 text-white/60 text-base leading-relaxed max-w-2xl">
        {CT.subtitle}
      </p>
      <div className="mt-4 max-w-2xl text-[11px] text-white/40 leading-relaxed border border-white/10 bg-black/40 rounded p-4">
        {HINT[L]}
      </div>

      {/* Selfie */}
      <div className="mt-10">
        <div className="text-xs tracking-luxe uppercase text-white/50 mb-3">{KIND.selfie}</div>
        <div className="rounded-md border border-white/10 bg-black/40 p-6">
          {docFor("selfie") && !captured && !camActive && (
            <div className="flex items-center gap-3 mb-4 text-emerald-300 text-sm" data-testid="kyc-selfie-saved">
              <Check size={16} /> {CT.saved}
              <button
                type="button"
                onClick={() => { startCamera(); }}
                className="ml-4 text-xs text-white/50 hover:text-white underline"
              >
                {CT.replace}
              </button>
            </div>
          )}
          {!camActive && !captured && !docFor("selfie") && (
            <button
              type="button"
              onClick={startCamera}
              data-testid="kyc-start-camera"
              className="inline-flex items-center gap-2 px-5 py-3 border border-[#e6c98a]/40 text-[#e6c98a] hover:bg-[#e6c98a] hover:text-black rounded transition-colors text-sm tracking-mid uppercase"
            >
              <Camera size={14} /> {CT.startCamera}
            </button>
          )}
          {camDenied && (
            <div className="mt-3 text-amber-300 text-xs flex items-center gap-2">
              <AlertCircle size={14} /> {CT.camDenied}
            </div>
          )}
          {camActive && (
            <div className="space-y-3">
              <video
                ref={videoRef}
                className="w-full max-w-md rounded border border-white/10"
                style={{ transform: "scaleX(-1)" }}
                muted
                playsInline
                data-testid="kyc-camera-preview"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={capture}
                  data-testid="kyc-capture"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#e6c98a] text-black rounded text-sm tracking-mid uppercase hover:bg-[#d4b46d]"
                >
                  <Camera size={14} /> {CT.capture}
                </button>
                <button type="button" onClick={stopCamera} className="text-sm text-white/60 hover:text-white">
                  {CT.cancel}
                </button>
              </div>
            </div>
          )}
          {captured && (
            <div className="space-y-3">
              <img
                src={captured}
                alt="captured"
                className="w-full max-w-md rounded border border-white/10"
                data-testid="kyc-captured-preview"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveSelfie}
                  disabled={savingSelfie}
                  data-testid="kyc-save-selfie"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#e6c98a] text-black rounded text-sm tracking-mid uppercase hover:bg-[#d4b46d] disabled:opacity-60"
                >
                  {savingSelfie ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {CT.save}
                </button>
                <button
                  type="button"
                  onClick={() => { setCaptured(null); startCamera(); }}
                  className="inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-white/70 hover:text-white hover:border-white/30 rounded text-sm tracking-mid uppercase"
                >
                  <RefreshCw size={14} /> {CT.retry}
                </button>
              </div>
            </div>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>
      </div>

      {/* File uploads */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {["id_front", "id_back", "company_registration", "proof_of_funds"].map((kind) => {
          const existing = docFor(kind);
          const busy = uploadingKind === kind;
          return (
            <div
              key={kind}
              className="rounded-md border border-white/10 bg-black/40 p-5"
              data-testid={`kyc-slot-${kind}`}
            >
              <div className="text-xs tracking-luxe uppercase text-white/50 mb-3">{KIND[kind]}</div>
              {existing ? (
                <div className="flex items-center gap-2 text-emerald-300 text-sm mb-3">
                  <Check size={14} /> {CT.uploaded}
                  <span className="text-white/40 text-xs ml-2 truncate max-w-[200px]">
                    {existing.original_filename}
                  </span>
                </div>
              ) : null}
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/80 hover:text-[#e6c98a] hover:border-[#e6c98a]/50 rounded text-xs tracking-mid uppercase cursor-pointer transition-colors">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {existing ? CT.replace : CT.upload}
                <input
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                  data-testid={`kyc-input-${kind}`}
                  onChange={(e) => uploadFile(kind, e.target.files?.[0])}
                />
              </label>
            </div>
          );
        })}
      </div>

      {error && (
        <div className="mt-6 text-rose-300 text-sm flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="mt-8">
        <Button
          onClick={loadDocs}
          disabled={refreshing}
          variant="ghost"
          className="text-xs text-white/50 hover:text-white"
          data-testid="kyc-refresh"
        >
          {refreshing ? <Loader2 size={12} className="animate-spin mr-2" /> : <RefreshCw size={12} className="mr-2" />}
          {CT.refresh}
        </Button>
      </div>
    </div>
  );
};
